import type { CardsResponse } from "@/lib/pb-types";
import { LANE_STATUSES, laneFor, type LaneStatus } from "./lanes";

/**
 * Pure board logic: grouping, ordering and issue keys.
 *
 * None of this touches React or PocketBase, which is what makes the two things
 * that are easy to get wrong — where a dropped card lands, and what number the
 * next TASK key gets — checkable in isolation.
 */

export type BoardCard = CardsResponse<unknown>;

export type LaneGroups = Record<LaneStatus, BoardCard[]>;

/* -------------------------------------------------------------------------- */
/* Reading a card                                                             */

/** `tags` is a free-form json column; anything that is not a string list reads as empty. */
export function cardTags(card: Pick<BoardCard, "tags">): string[] {
  const { tags } = card;
  if (Array.isArray(tags)) {
    return tags.filter((tag): tag is string => typeof tag === "string" && tag.trim() !== "");
  }
  if (typeof tags === "string" && tags.trim() !== "") {
    return tags
      .split(",")
      .map((tag) => tag.trim())
      .filter(Boolean);
  }
  return [];
}

/**
 * The title without a leading issue key.
 *
 * Imported titles are sometimes written as "TASK-12 — Do the thing" while the
 * key is also its own column; the tile already shows the key as a chip, so
 * repeating it inside the title just wastes the one line that matters.
 */
export function cardTitle(card: Pick<BoardCard, "title" | "issue_key">): string {
  const title = card.title.trim();
  const key = card.issue_key.trim();
  if (!key || !title.startsWith(key)) {
    return title;
  }
  return title.slice(key.length).replace(/^[\s—–:.-]+/, "").trim() || title;
}

/* -------------------------------------------------------------------------- */
/* Ordering                                                                   */

/**
 * Gap left between neighbouring cards when a lane is renumbered.
 *
 * Ordering is an integer column, so a drop between two adjacent numbers has
 * nowhere to go and forces a renumber of the whole lane. Spacing the lane out
 * once buys ~6 further drops at that spot for a single write each, which is the
 * difference between one PATCH and ninety-five on a heavy board.
 */
export const ORDER_STEP = 64;

export type Ordered = { id: string; order: number };

/** The number in `TASK-12`, or null when the key is not of that shape. */
export function issueNumber(issueKey: string): number | null {
  const match = /^[A-Za-z]+-(\d+)/.exec(issueKey.trim());
  if (!match) {
    return null;
  }
  const value = Number.parseInt(match[1]!, 10);
  return Number.isFinite(value) ? value : null;
}

/**
 * Display order inside a lane: `order` first, then the issue number, then the
 * id. The tie-breakers matter — imported rows can share an order, and a lane
 * that reshuffles itself on every refetch looks broken even though nothing moved.
 */
export function compareCards(a: BoardCard, b: BoardCard): number {
  const orderA = a.order ?? 0;
  const orderB = b.order ?? 0;
  if (orderA !== orderB) {
    return orderA - orderB;
  }
  const numberA = issueNumber(a.issue_key);
  const numberB = issueNumber(b.issue_key);
  if (numberA !== null && numberB !== null && numberA !== numberB) {
    return numberA - numberB;
  }
  return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
}

/**
 * Every card of a project, bucketed into the seven lanes and sorted.
 */
export function groupByLane(cards: readonly BoardCard[]): LaneGroups {
  const groups = Object.fromEntries(
    LANE_STATUSES.map((status) => [status, [] as BoardCard[]]),
  ) as unknown as LaneGroups;
  for (const card of cards) {
    groups[laneFor(card.status).status].push(card);
  }
  for (const status of LANE_STATUSES) {
    groups[status].sort(compareCards);
  }
  return groups;
}

export type InsertPlan = {
  /** The order value the inserted card should be given. */
  order: number;
  /** Other cards in the lane whose order has to be rewritten to make room. */
  renumber: Ordered[];
};

/**
 * Where an insertion at `index` lands, and what it costs.
 *
 * `lane` is the destination lane **without** the card being inserted, already
 * sorted. The happy path finds an integer between the two neighbours and writes
 * exactly one row. Only when the neighbours are adjacent integers (or the head
 * of the lane is already at 0) does the whole lane get respaced — and that
 * respacing is what stops it happening again at the same spot.
 */
export function planInsert(lane: readonly Ordered[], index: number): InsertPlan {
  const at = Math.max(0, Math.min(index, lane.length));
  const lower = at > 0 ? lane[at - 1]!.order : null;
  const upper = at < lane.length ? lane[at]!.order : null;

  if (lower === null && upper === null) {
    return { order: ORDER_STEP, renumber: [] };
  }
  if (lower === null && upper !== null) {
    if (upper >= 2) {
      return { order: Math.floor(upper / 2), renumber: [] };
    }
  } else if (upper === null && lower !== null) {
    return { order: lower + ORDER_STEP, renumber: [] };
  } else if (lower !== null && upper !== null && upper - lower >= 2) {
    return { order: lower + Math.floor((upper - lower) / 2), renumber: [] };
  }

  // No room: respace the lane, leaving a slot at `at` for the incoming card.
  const renumber: Ordered[] = [];
  let order = 0;
  let slot = ORDER_STEP;
  for (let i = 0; i <= lane.length; i += 1) {
    order += ORDER_STEP;
    if (i === at) {
      slot = order;
      continue;
    }
    const card = lane[i > at ? i - 1 : i]!;
    if (card.order !== order) {
      renumber.push({ id: card.id, order });
    }
  }

  return { order: slot, renumber };
}

export type MovePlan = {
  card: BoardCard;
  status: LaneStatus;
  order: number;
  renumber: Ordered[];
  /** True when the drop would leave the board exactly as it is. */
  noop: boolean;
};

/**
 * Turn "this card was dropped into that lane at that position" into the set of
 * writes it implies. `index` counts positions in the destination lane with the
 * dragged card already taken out of it.
 */
export function planMove(
  groups: LaneGroups,
  card: BoardCard,
  status: LaneStatus,
  index: number,
): MovePlan {
  const destination = groups[status].filter((entry) => entry.id !== card.id);
  const at = Math.max(0, Math.min(index, destination.length));

  if (card.status === status) {
    const currentIndex = groups[status].findIndex((entry) => entry.id === card.id);
    const currentAdjusted = currentIndex < 0 ? -1 : Math.min(currentIndex, destination.length);
    if (currentAdjusted === at) {
      return { card, status, order: card.order ?? 0, renumber: [], noop: true };
    }
  }

  const plan = planInsert(destination, at);
  return { card, status, order: plan.order, renumber: plan.renumber, noop: false };
}

/** Apply a move to a card list without waiting for the server. */
export function applyMove(cards: readonly BoardCard[], plan: MovePlan): BoardCard[] {
  const renumbered = new Map(plan.renumber.map((entry) => [entry.id, entry.order]));
  return cards.map((card) => {
    if (card.id === plan.card.id) {
      return { ...card, status: plan.status, order: plan.order };
    }
    const order = renumbered.get(card.id);
    return order === undefined ? card : { ...card, order };
  });
}

/**
 * Translate a drop position measured against a filtered lane into a position in
 * the real one.
 *
 * The board can be dragged while it is filtered — that is half the point of
 * filtering — but the cards on screen are then a subset, so "index 2" means
 * "before whichever card is third *visible*", not "third in the lane".
 */
export function unfilterIndex(
  fullLane: readonly BoardCard[],
  visibleLane: readonly BoardCard[],
  index: number,
  excludeId: string,
): number {
  const full = fullLane.filter((card) => card.id !== excludeId);
  const visible = visibleLane.filter((card) => card.id !== excludeId);
  const anchor = visible[Math.max(0, index)];
  if (!anchor) {
    return full.length;
  }
  const at = full.findIndex((card) => card.id === anchor.id);
  return at < 0 ? full.length : at;
}

/* -------------------------------------------------------------------------- */
/* Issue keys                                                                 */

export const ISSUE_PREFIX = "TASK";

/**
 * The next free key for a project.
 *
 * Keys are unique per project, not globally, so this only ever looks at the
 * cards already loaded for the board it is minting into. The unique index is
 * still the real guard — two tabs can race — which is why the caller retries on
 * a rejected create rather than trusting this number.
 */
export function nextIssueKey(cards: readonly BoardCard[], prefix = ISSUE_PREFIX): string {
  let highest = 0;
  for (const card of cards) {
    const number = issueNumber(card.issue_key);
    if (number !== null && number > highest) {
      highest = number;
    }
  }
  return `${prefix}-${highest + 1}`;
}

/* -------------------------------------------------------------------------- */
/* Filtering                                                                  */

/** Everything on a card that the board's filter box searches. */
export function matchesQuery(card: BoardCard, query: string): boolean {
  const needle = query.trim().toLowerCase();
  if (!needle) {
    return true;
  }
  const haystack = [
    card.issue_key,
    card.title,
    card.description,
    card.owner,
    card.type,
    card.priority,
    ...cardTags(card),
  ];
  return haystack.some((value) => typeof value === "string" && value.toLowerCase().includes(needle));
}

/**
 * A URL-safe slug for a new project, matching the schema's pattern.
 *
 * NFKD splits an accented letter into its base plus a combining mark; the mark
 * is then dropped rather than left to become a separator, so "Münster" slugs to
 * `munster` and not `mu-nster`.
 */
export function slugify(name: string): string {
  return name
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}
